import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { useTransition, animated } from 'react-spring'
import usePrevious from 'hooks/usePrevious'
import classes from './ModalRenderer.module.scss'
import _ from 'lodash'
import { HotKeys } from 'react-hotkeys'
import styled from 'styled-components'
import navigationController from './navigationController'
import { useDrop } from 'react-dnd'

const KEY_MAP = {
  'closeAll': 'escape',
}

const getId = view => view.id

const ModalOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: rgba(255,255,255,0.5);
  }
`

const backdropSpec = {
  accept: ['LABEL', 'DOCUMENT', 'USER'],
  collect: (monitor) => ({
    item: monitor.getItem(),
    isOver: monitor.isOver({ shallow: true }),
  })
}

const Backdrop = ({
  onDragStart,
  onDragEnd,
  ...props
}) => {
  const dragStartTimeoutRef = useRef<ReturnType<typeof setTimeout>>() // TODO: We have to delay drag event because react-dnd returns wrong offset at the beginning

  const [{ item, isOver }, drop] = useDrop(backdropSpec)

  useEffect(() => {
    if (item) {
      if (isOver) {
        dragStartTimeoutRef.current = setTimeout(() => {
          onDragStart?.()
        }, 100)
      } else {
        dragStartTimeoutRef.current && clearTimeout(dragStartTimeoutRef.current)
      }
    } else {
      dragStartTimeoutRef.current && clearTimeout(dragStartTimeoutRef.current)
      onDragEnd?.()
    }
  }, [item, isOver])

  return (
    <animated.div
      {...props}
      ref={drop}
      className={classes.backdrop}
    />
  )
}

const ModalRenderer = () => {
  const views = navigationController.useViews()
  const prevViews = usePrevious<any[]>(views)
  
  const { setViews } = navigationController.useActions()

  const closeAll = useCallback(() => {
    setViews([])
  }, [setViews])

  useEffect(() => {
    if (views && prevViews) {
      const removedIds = _.difference(prevViews.map(getId), views.map(getId))

      let i
      for (i = prevViews.length - 1; i >= 0; i--) {
        if (removedIds.includes(prevViews[i].id)) {
          prevViews[i].onClose?.()
        }
      }
    }
  }, [views])

  const [transitionEnd, setTransitionEnd] = useState({})
  const [hidden, setHidden] = useState(false)

  const mappedViews = useMemo(() => {
    return views.map(view => ({
      ...view,
      transitionEnd: transitionEnd[view.id] || false,
      hidden,
    }))
  }, [views, transitionEnd, hidden])

  const backdropTransition = useTransition(mappedViews, {
    key: view => view.id,
    from: { opacity: 0, pointerEvents: 'initial' },
    leave: { opacity: 0, pointerEvents: 'none' as React.CSSProperties['pointerEvents'] },
    enter: { opacity: 1 },
    update: view => ({
      opacity: view.hidden ? 0 : 1,
      pointerEvents: view.hidden ? 'none' : 'initial'
    }),
    config: {
      tension: 300,
      clamp: true,
    }
  })

  const transition = useTransition(mappedViews, {
    key: view => view.id,
    from: view => ({
      position: 'absolute',
      opacity: 0,
      transform: 'scale(0)',
      transformOrigin: (() => {
        switch (view.position) {
          case 'left':
            return '25% center'
          case 'right':
            return '75% center'
          default:
            return 'center'
        }
      })()   
    }),
    leave: view => ({
      opacity: 0,
      transform: `
        scale(0)
        translateX(${view.hidden ? (view.position === 'left' ? -100 : (view.position === 'right' ? 100 : 0)) : 0}vw)
        translateY(${(view.hidden && view.position === 'center') ? 100 : 0}vh)
      `
    }),
    enter: view => ({
      opacity: 1,
      transform: `scale(${Math.max(0.1, 1 - (mappedViews.length - mappedViews.indexOf(view) - 1) / 10)})`
    }),
    update: view => ({
      opacity: 1,
      transform: `
        scale(${Math.max(0.1, 1 - (mappedViews.length - mappedViews.indexOf(view) - 1) / 10)})
        translateX(${view.hidden ? (view.position === 'left' ? -100 : (view.position === 'right' ? 100 : 0)) : 0}vw)
        translateY(${(view.hidden && view.position === 'center') ? 100 : 0}vh)
      `
    }),
    onRest: (result, state) => { setTransitionEnd({ ...transitionEnd, [state.item.id]: true }) },
    config: {
      tension: 300,
      clamp: true,
    }
  })

  const keyHandlers = useMemo(() => ({
    closeAll
  }), [closeAll])

  return (
    <React.Fragment>
      {mappedViews.length > 0 &&
        <HotKeys
          focused
          keyMap={KEY_MAP}
          handlers={keyHandlers}
          attach={window}
        />
      }
      {backdropTransition((values, view, state, index) =>
        <Backdrop
          style={{ ...values, zIndex: 20 + index * 2, ...(index > 0 ? { pointerEvents: 'none' } : {}) }}
          onClick={index === 0 ? closeAll : undefined}
          onDragStart={index === 0 ? () => setHidden(true) : undefined}
          onDragEnd={index === 0 ? () => setHidden(false) : undefined}
        />
      )}
      {transition((values, view, state, index) =>
        <div
          style={{
            zIndex: 21 + index * 2,
            alignItems: (() => {
              if (view.position === 'left') {
                return 'flex-start'
              }

              if (view.position === 'right') {
                return 'flex-end'
              }
              
              return 'center'
            })()
          }}
          className={`${classes.modalWrapper}${view.maximized ? ` ${classes.maximized}` : ''}`}
        >
          <animated.div
            style={{ ...values, position: 'absolute' } as any}
            onClick={event => { event.stopPropagation() }}
          > 
            {index !== mappedViews.length - 1 &&
              <ModalOverlay
                onClick={event => {
                  setViews(
                    mappedViews.slice(0, index + 1)
                    .map(({ component, ...options }) => ({
                      component,
                      options: {
                        ...options,
                      }
                    }))
                  )
                }}
              />
            }
            {React.isValidElement(view.component)
              ? React.cloneElement(view.component, { transitionEnd: view.transitionEnd, modalId: view.id, modalMaximized: view.maximized })
              : <view.component transitionEnd={view.transitionEnd} modalId={view.id} modalMaximized={view.maximized} />
            }
          </animated.div>
        </div>
      )}
    </React.Fragment>
  )
}

export default ModalRenderer
